import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import type { CouponPreview } from '@lov/shared';

interface AvailableCouponsDrawerProps {
  open: boolean;
  onClose: () => void;
  coupons: CouponPreview[];
  isLoading?: boolean;
  onApplyCoupon: (code: string) => void;
}

export function AvailableCouponsDrawer({
  open,
  onClose,
  coupons,
  isLoading,
  onApplyCoupon,
}: AvailableCouponsDrawerProps) {
  const handleApply = (code: string) => {
    onApplyCoupon(code);
    onClose();
  };

  const formatCurrency = (amount: number, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Available Coupons
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : coupons.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <LocalOfferIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No coupons available for this booking
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '60vh', overflow: 'auto' }}>
            {coupons.map((coupon) => (
              <Paper
                key={coupon.code}
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <LocalOfferIcon fontSize="small" color="primary" />
                      <Typography variant="body1" fontWeight={700} color="primary.main">
                        {coupon.code}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {coupon.name}
                    </Typography>
                    {coupon.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {coupon.description}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleApply(coupon.code)}
                    sx={{ ml: 2 }}
                  >
                    Apply
                  </Button>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {coupon.discountText}
                  </Typography>
                  {coupon.estimatedSavings && (
                    <Typography variant="caption" color="text.secondary">
                      Save {formatCurrency(coupon.estimatedSavings)}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
